/**
 * Admin Backup Page
 */
import { useLanguage } from "@/context/AppContext";
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Server, ChevronLeft, RefreshCw, Loader2, Database, Users, Briefcase, MessageSquare, CreditCard, CheckCircle, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBackup() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [backupInfo, setBackupInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchBackupInfo();
  }, []);

  const fetchBackupInfo = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/backup`, { withCredentials: true });
      setBackupInfo(res.data);
    } catch (err) {
      toast.error('Fehler beim Laden der Backup-Informationen');
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/admin/backup/create`, {}, { withCredentials: true });
      toast.success('Backup erfolgreich erstellt!');
      fetchBackupInfo();
    } catch (err) {
      toast.error('Fehler beim Erstellen des Backups');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-CH', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const stats = backupInfo?.database_stats || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/admin')} className="rounded-full">
          <ChevronLeft className="w-4 h-4 mr-1" />Zurück
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{t('adminPageBackup')}</h1>
          <p className="text-gray-500">Datensicherung und Statistiken verwalten</p>
        </div>
        <Button variant="outline" onClick={fetchBackupInfo}>
          <RefreshCw className="w-4 h-4 mr-2" />Aktualisieren
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
        </div>
      ) : (
        <>
          {/* Database Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                Datenbank-Statistiken
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <Users className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                  <p className="text-2xl font-bold">{stats.users || 0}</p>
                  <p className="text-sm text-gray-600">Benutzer</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <Briefcase className="w-8 h-8 mx-auto text-green-600 mb-2" />
                  <p className="text-2xl font-bold">{stats.jobs || 0}</p>
                  <p className="text-sm text-gray-600">Aufträge</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <MessageSquare className="w-8 h-8 mx-auto text-purple-600 mb-2" />
                  <p className="text-2xl font-bold">{stats.messages || 0}</p>
                  <p className="text-sm text-gray-600">Nachrichten</p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg text-center">
                  <Briefcase className="w-8 h-8 mx-auto text-yellow-600 mb-2" />
                  <p className="text-2xl font-bold">{stats.bids || 0}</p>
                  <p className="text-sm text-gray-600">Angebote</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <CreditCard className="w-8 h-8 mx-auto text-red-600 mb-2" />
                  <p className="text-2xl font-bold">{stats.payments || 0}</p>
                  <p className="text-sm text-gray-600">{t('adminPayments')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Backup Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="w-5 h-5" />
                Backup erstellen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  Ein Backup sichert alle wichtigen Daten: Benutzer, Aufträge, Nachrichten, Angebote und Zahlungen.
                </p>
              </div>
              
              <Button 
                onClick={createBackup} 
                disabled={creating}
                className="bg-[#0044CC] hover:bg-[#003399]"
              >
                {creating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Backup wird erstellt...</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" />Manuelles Backup erstellen</>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Last Backup Info */}
          <Card>
            <CardHeader>
              <CardTitle>Letztes Backup</CardTitle>
            </CardHeader>
            <CardContent>
              {backupInfo?.last_backup ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <Badge className="bg-green-100 text-green-800">{backupInfo.last_backup.status}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Backup ID</p>
                      <p className="font-mono">{backupInfo.last_backup.backup_id}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Erstellt am</p>
                      <p>{formatDate(backupInfo.last_backup.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Typ</p>
                      <p className="capitalize">{backupInfo.last_backup.type}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Sammlungen</p>
                      <p>{backupInfo.last_backup.collections?.join(', ')}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Server className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Noch kein Backup erstellt</p>
                  <p className="text-sm mt-2">Erstellen Sie Ihr erstes Backup</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
